
import { assetUrl } from '@/utils/asset-url'
import { ReactComponent as Error } from '@/assets/svgs/icon-error-message-exclamation.svg'
import { ReactComponent as IconTick } from '@/assets/svgs/icon-account-setting-tick.svg'
import { ReactComponent as IconDefaultCompanyLogo } from '@/assets/svgs/company-logo-icon.svg'
import { ReactComponent as IconUploadInfo } from "@/assets/svgs/icon-ds-info.svg";
import iconBuilding from '@/assets/svgs/icon-building.svg'
import Modal, { MEDIUM_MODAL_WIDTH } from '@/dsComponents/Modal'
import ModalTemplate from '@/dsComponents/ModalTemplate'
import FileUpload from '@/dsComponents/fileUpload'
import useModal from '@/hooks/use-modal'
import rem from '@/utils/rem'
import React, { useState, useCallback, useEffect } from 'react'
import DOMPurify from 'dompurify'
import { useForm } from 'react-hook-form'
import { validateEmail } from '@/utils/validate-email'
import classname from 'classnames'
import Button from '@/components/Button'
import API from '@/services/api/login.js'
import API_ACCOUNT from '@/services/api/accountSetting.js'
import Toast from '@/components/Toast'
import useToast from '@/hooks/use-toast'
import Input from '@/components/Input/index'
import validatePassword from '@/utils/validate-password'
import { feature, features as featureConstant } from '@/utils/feature-verifier'
import Tooltip from '@/components/Tooltip/Tooltip'
import API_PROFILE from '@/services/api/profile.js';
import { useTradex } from '@/hooks/use-tradex/index'
import ModelTermsTable from './modal-terms-table'
import { useFeature } from '@/hooks/use-feature/index'
import { hasUsersUpdatePermission } from '@/utils/permissions'
import PropTypes from 'prop-types';
import { Box, FileUploadDropZone, FileUploadList, ModalBody, ModalFooter, ModalFooterLeft, ModalFooterRight, ModalHeader, ModalTitle } from '@dextech-ai/dex-design-system';
import { InfoOutlined, SettingsOutlined } from '@dextech-ai/dex-design-system/icons';

const SHELL_HOSTED = process.env.REACT_APP_SHELL_HOSTED === 'true';

const Page = ({setCreateModelTerm, modelTermEnabled, termTemplate}) => {

	/*=================================================
		General
	/================================================*/
	const [profile, setProfile] = useState({});
	const isTradex = useTradex()
	// In the unified portal (shell-hosted merged page), only surface the password
	// Change action to users whose role can actually update it (Users.Update);
	const canChangePassword = !SHELL_HOSTED || hasUsersUpdatePermission();

	useEffect(() => {
		async function fetchProfileDetails() {
			await getProfileDetails();
		}
		fetchProfileDetails();
	}, []);

	async function getProfileDetails() {
		const result = await API_PROFILE.index();
		if (!result) {
			return addToastMessage("Requests API has error", "error");
		}
		const [error, response] = result;
		if (error) {
			return addToastMessage("Requests API has error", "error");
		}
		return setProfile(response);
	}

	const [loading, setLoading] = useState(false);
	const [fileLists, setFileLists] = useState([]);

	const passwordMinLength = 12;

	/*=================================================
		Modal
	/================================================*/

	const { toastList, addToastMessage } = useToast()

	const { visible: visibleVerifyUser, show: showVerifyUser, hide: hideVerifyUser } = useModal()
	const { visible: visibleUpdateEmail, show: showUpdateEmail, hide: hideUpdateEmail } = useModal()
	const { visible: visibleUpdateEmailSuccess, show: showUpdateEmailSuccess, hide: hideUpdateEmailSuccess } = useModal()
	const { visible: visibleUpdatePassword, show: showUpdatePassword, hide: hideUpdatePassword } = useModal()
	const { visible: visibleUpdatePasswordSuccess, show: showUpdatePasswordSuccess, hide: hideUpdatePasswordSuccess } = useModal()
	const { visible: visibleUploadCompanyLogo, show: showUploadCompanyLogo, hide: hideUploadCompanyLogo } = useModal()

	const isIGPNILegalRequirementsEnabled = useFeature(featureConstant.IG_PNI_LEGAL_REQUIREMENTS)

	/*=================================================
		Dropzone for Letterhead Image
	/================================================*/
	const sanitizeSVG = (file) => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.onload = (e) => {
				try {
					const sanitized = DOMPurify.sanitize(e.target.result, {
						USE_PROFILES: { svg: true, svgFilters: true },
						ADD_TAGS: ['use'],
					});
					const blob = new Blob([sanitized], { type: 'image/svg+xml' });
					const sanitizedFile = new File([blob], file.name, { type: 'image/svg+xml' });
					Object.assign(sanitizedFile, { preview: URL.createObjectURL(blob) });
					resolve(sanitizedFile);
				} catch (error) {
					reject(error);
				}
			};
			reader.onerror = () => reject(new Error('Failed to read SVG file'));
			reader.readAsText(file);
		});
	};

	const [logoError, setLogoError] = useState(null);

	const handleLogoFilesChange = useCallback(async (files) => {
		const file = files[0];
		if (!file) {
			return setFileLists([]);
		}

		const isSVG = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg');

		try {
			let processedFile;
			if (isSVG) {
				processedFile = await sanitizeSVG(file);
			} else {
				processedFile = Object.assign(file, { preview: URL.createObjectURL(file) });
			}
			setFileLists([processedFile]);
		} catch (error) {
			console.error('SVG sanitization failed:', error);
			return setLogoError("Failed to process file");
		}
	}, []);

	const { register, handleSubmit, formState: { errors, isValid }, getValues, setValue, watch, trigger } = useForm({
		mode: 'onChange'
	})

	const toolTipContent = `Upload your company's official letterhead image. <br /> This image will be displayed at the top of all generated ETR documents. <br /> Recommended image aspect ratio: 3:1 width:height.`;

	//Watch password fields and trigger validation on the other field
	const watchNewPassword = watch('newPassword');
	const watchVerifyPassword = watch('verifyPassword');

	useEffect(() => {
		if (watchNewPassword && watchVerifyPassword) {
			trigger('verifyPassword');
		}
	}, [watchNewPassword]);

	useEffect(() => {
		if (watchNewPassword && watchVerifyPassword) {
			trigger('newPassword');
		}
	}, [watchVerifyPassword]);

	async function requestAction() {
		setLoading(true);
		const [getUrlError, urlResult] = await API_ACCOUNT.getSignedUrl(profile.organization.orgId, fileLists, 'document-logo')
		if (getUrlError) {
			setLoading(false);
			return addToastMessage(getUrlError?.message, "error")
		}
		const [uploadError] = await API_ACCOUNT.uploadFileToSignedURL(urlResult.uploadURL, fileLists)
		if (uploadError) {
			setLoading(false);
			return addToastMessage(uploadError?.message, "error")
		}
		const body = {
			key: urlResult.key,
			fileName: fileLists[0]?.name
		}
		const [documentError, documentResponse] = await API_ACCOUNT.uploadDocumentLogo(profile.organization.orgId, body)
		if (documentError) {
			setLoading(false);
			return addToastMessage(documentError?.message, "error")
		}
		setLoading(false);
		getProfileDetails();
		hideUploadCompanyLogo();
		return addToastMessage(documentResponse?.message, "success")
	}

	const [error, setError] = useState(null)
	const [verifyUser, setVerifyUser] = useState(null)
	const [newEmail, setNewEmail] = useState(null)

	function resetValue() {
		setValue('currentPassword', "");
		setValue('newPassword', "");
		setValue('verifyPassword', "");
	}

	const inputClass = classname({
		'input--error': error
	})

	async function verifyUserHandler({ password }) {
		if (password === "") {
			return setError("Required")
		}

		const [error, response] = await API.create({ email: profile.email, password: password })

		if (error) {
			return setError('Incorrect password')
		}

		hideVerifyUser()
		showUpdateEmailModal()
	}

	function showUpdateEmailModal() {
		setError(null)
		setNewEmail(null)
		showUpdateEmail()
	}

	function updateEmailHandler({ email }) {
		if (email === "") {
			return setError("Required")
		}
		if (!validateEmail(email)) {
			return setError("Please provide a valid email")
		}
		hideUpdateEmail()
		showUpdateEmailSuccess()
	}

	function showUpdatePasswordModal() {
		setError(null)
		showUpdatePassword()
	}

	async function updatePasswordHandler(data) {
		if (loading) {
			return
		}

		setLoading(true)

		const currentPassword = data.currentPassword
		const newPassword = data.newPassword
		setError(null)

		const [errorResetPassword, responseResetPassword] = await API_ACCOUNT.update(profile.id, { password: newPassword, currentPassword: currentPassword })

		setLoading(false)
		if (errorResetPassword) {
			resetValue()
			return setError('Failed to update password')
		}

		addToastMessage("Your password has been updated.", "success")

		hideUpdatePassword()
		showUpdatePasswordSuccess()
	}

	const profileImage = profile?.organization?.iconUrl ? profile?.organization?.iconUrl : iconBuilding

	return (
		<>
			<div className="account-setting-box__layout">
				<Box display="flex" align="center">
					<div className="account-setting-box__top__background">
						<div className="account-setting-avatar__top__image" style={{ backgroundImage: `url(${profileImage})`, textAlign: 'center' }}></div>
					</div>
					<div className="account-setting-box__top__text">
						<p className="account-setting-box__top__text__name">{profile?.firstName}</p>
						<p className="account-setting-box__top__text__org">{profile?.organization?.name}</p>
					</div>
				</Box>
				<div className="account-setting-box__bottom">
					<div className="account-setting-box__bottom__email">
						<span className="account-setting-box__bottom__text">Email Address</span>
						<input type="text" placeholder={profile?.email} style={{ width: rem(368) }} disabled={true} />
					</div>
					<div className="account-setting-box__bottom__password">
						<span className="account-setting-box__bottom__text">Password</span>
						<input type="password" style={{ width: rem(368) }} placeholder="*************" disabled={true} />
						{canChangePassword && <Button ghost text="Change" submit={() => showUpdatePasswordModal()} />}
					</div>
					{feature(featureConstant.ETR_LOGO_RENDERER) && isTradex && <div className="account-setting-box__bottom__company-logo">
						<span className="account-setting-box__bottom__company-text">
							Letterhead Image <div data-tooltip-id="tooltip" data-tooltip-html={toolTipContent} data-tooltip-place='bottom-end'><IconUploadInfo /></div>
						</span>
						{profile?.organization?.documentLogo ? <div>
							<div className='account-setting-box__bottom__company-logo__renderer'>
								<div className='account-setting-box__bottom__company-icon'>
									<img src={`https://blob.sgtradex.io/${profile?.organization.documentLogo.key}`} alt="Letterhead" />
								</div>
								<div className='account-setting-box__bottom__company-name'>{profile?.organization.documentLogo.fileName}</div>
							</div>
							<div className='account-setting-box__bottom__format-text'>Supported Format: PNG, or SVG. Maximum 500kB per file</div>
						</div> : <div>
							<div className='account-setting-box__bottom__company-logo__icon-text'>
								<IconDefaultCompanyLogo />
								<span>No image uploaded yet</span>
							</div>
							<div className='account-setting-box__bottom__format-text'>Supported Format: PNG, or SVG. Maximum 500kB per file</div>
						</div>}
						<Button
							ghost
							data-testid="addImage"
							text={profile?.organization?.documentLogo ? "Change" : "Add Image"}
							submit={() => {
								showUploadCompanyLogo();
								setFileLists([]);
							}}
						/>
					</div>}
				</div>
				{isIGPNILegalRequirementsEnabled && modelTermEnabled && (
					<ModelTermsTable setCreateModelTerm={setCreateModelTerm} termTemplate={termTemplate}/>
				)}
			</div>

			<Modal visible={visibleVerifyUser} close={hideVerifyUser} dismissClickOutsideModal={false}>
				<ModalHeader icon={<SettingsOutlined color="info" />}>
					<ModalTitle>Update Email Address</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<form id="verifyUser" onSubmit={handleSubmit(verifyUserHandler)}>
						<div className="account-setting-box__modal__col__email">
							<div className="account-setting-box__modal__desc" >To continue, please verify your identity</div>
							<div style={{ marginTop: rem(6) }}>
								<input name="password" className={inputClass} type="password" placeholder="Enter current password" ref={register({ required: true })} onChange={(e) => setVerifyUser(e.target.value)} />
								{error && (
									<div className="t--required account-setting-box__modal__error">
										<div><Error /></div>
										<div>{error}</div>
									</div>
								)}
							</div>
						</div>
					</form>
				</ModalBody>
				<ModalFooter>
					<ModalFooterRight>
						<Button ghost text="Cancel" submit={() => hideVerifyUser()} />
						<Button form="verifyUser" text="Confirm" disabled={!verifyUser} />
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			<Modal visible={visibleUpdateEmail} close={hideUpdateEmail} dismissClickOutsideModal={false}>
				<ModalHeader icon={<SettingsOutlined color="info" />}>
					<ModalTitle>Update Email Address</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<form id="updateEmail" onSubmit={handleSubmit(updateEmailHandler)}>
						<div className="account-setting-box__modal__col__email">
							<div className="account-setting-box__modal__desc" >Enter a New Email Address</div>
							<div style={{ marginTop: rem(6) }}>
								<input name="email" className={inputClass} type="text" placeholder="Type here" ref={register({ required: true })} onChange={(e) => setNewEmail(e.target.value)} autoComplete="off" />
								{error && (
									<div className="t--required account-setting-box__modal__error">
										<div><Error /></div>
										<div>{error}</div>
									</div>
								)}
							</div>
						</div>
					</form>
				</ModalBody>
				<ModalFooter>
					<ModalFooterRight>
						<Button ghost text="Cancel" submit={() => hideUpdateEmail()} />
						<Button form="updateEmail" disabled={!newEmail} text="Confirm" />
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			<Modal visible={visibleUpdateEmailSuccess} close={hideUpdateEmailSuccess} dismissClickOutsideModal={false}>
				<ModalHeader icon={<SettingsOutlined color="info" />}>
					<ModalTitle>Update Email Address</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<div className="account-setting-box__modal__success__email">
						<div style={{ marginBottom: rem(14) }}><IconTick /></div>
						<div className="account-setting-box__modal__success__text">
							<p>Your email is updated.</p>
							<p>We sent a mail to {newEmail} for you to verify.</p>
						</div>
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalFooterRight>
						<Button ghost text="Close" submit={() => hideUpdateEmailSuccess()} />
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			<Modal visible={visibleUpdatePassword} close={hideUpdatePassword} dismissClickOutsideModal={false}>
				<ModalHeader icon={<SettingsOutlined color="info" />}>
					<ModalTitle>Change Password</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<form id="updatePassword" onSubmit={handleSubmit(updatePasswordHandler)}>
						<div className="account-setting-box__modal__cols">
							<div className="account-setting-box__modal__col">
								<Input
									name="currentPassword"
									label="Current Password"
									register={register({ required: "Required" })}
									placeholder="Type here"
									type="Password"
								/>
							</div>
							<div className="account-setting-box__modal__col">
								<Input
									name="newPassword"
									label="New Password"
									register={register({
										required: "Required",
										validate: {
											checkLength: v => v.length >= passwordMinLength || "Error",
											verifyPasswordRequirement: v => validatePassword(v) || "Error",
											verifyPassword2: v => v !== getValues().currentPassword || "The new password you entered is the same as the old password. Please enter a different password",
											checkPassword: v => v !== profile?.email || "Your password cannot be the same as your username. Please enter a different password.",
											matchConfirmPassword: v => !getValues().verifyPassword || v === getValues().verifyPassword || "Passwords do not match"
										}
									})}
									placeholder="Type here"
									type="password"
								/>
							</div>
							<div className="account-setting-box__modal__col">
								<Input
									name="verifyPassword"
									label="Confirm New Password"
									register={register({
										required: "Required",
										validate: {
											verifyPassword3: v => v === getValues().newPassword || "Passwords do not match"
										}
									})}
									placeholder="Type here"
									error={errors.verifyPassword}
									errorMessage={errors.verifyPassword?.message}
									type="password"
								/>
							</div>

							{errors.newPassword && errors.newPassword?.message !== "Error" && (
								<div className="account-setting-box__modal__col resetPassword-box__caption">
									{errors.newPassword?.message}
								</div>
							)}
							{error && (
								<div className="account-setting-box__modal__col resetPassword-box__caption">
									{error}
								</div>
							)}

							<div className="account-setting-box__modal__col resetPassword-box__caption">
								New password must contain minimum {passwordMinLength} characters and at least 1 uppercase letter, lowercase letter, symbol, and number.
							</div>
						</div>
					</form>
				</ModalBody>
				<ModalFooter>
					<ModalFooterLeft>
						<Button text="Cancel" ghost noborder submit={() => hideUpdatePassword()} />
					</ModalFooterLeft>
					<ModalFooterRight>
						<Button form="updatePassword" round disabled={!isValid} loading={loading} text="Confirm" />
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			<Modal visible={visibleUpdatePasswordSuccess} close={hideUpdatePasswordSuccess} dismissClickOutsideModal={false}>
				<ModalHeader icon={<SettingsOutlined color="info" />}>
					<ModalTitle>Change Password</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<div className="account-setting-box__modal__success__password">
						<div style={{ marginBottom: rem(24) }}><IconTick /></div>
						<p>Your password has been changed.</p>
					</div>
				</ModalBody>
				<ModalFooter>
					<ModalFooterRight>
						<Button ghost text="Close" submit={() => hideUpdatePasswordSuccess()} />
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			<Modal width={MEDIUM_MODAL_WIDTH} visible={visibleUploadCompanyLogo} close={hideUploadCompanyLogo} dismissClickOutsideModal={false}>
				<ModalHeader icon={<InfoOutlined data-ds-color="primary-dark" />}>
					<ModalTitle>Add Image</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<div className="account-settings__modal-logo__heading">
						<div className="account-settings__modal-logo__heading-details-left">
							<div className="account-settings-modal-logo__company-text">Letterhead Image
								<div data-tooltip-id="tooltip" data-tooltip-html={toolTipContent}><IconUploadInfo /></div>
							</div>
						</div>
					</div>
					<FileUpload
						files={fileLists}
						onFilesChange={handleLogoFilesChange}
						accept="image/png,.svg"
						typeErrorMessage="Invalid file format. Please upload a PNG or SVG file."
						maxFiles={1}
						maxFileSize={500000}
						sizeErrorMessage="File uploaded size is more than 500kb"
						onError={setLogoError}
						disabled={loading}
						dataTestId="input-fileupload"
					>
						<FileUploadDropZone label="Drop your attachment here or browse" description="Supported Format: PNG, or SVG. Maximum 500kB per file" />
						<FileUploadList listType="picture" />
					</FileUpload>
					<Tooltip />
				</ModalBody>
				<ModalFooter>
					<ModalFooterLeft>
						<Button text="Cancel" ghost noborder data-testid="company-logo-cancel" submit={() => hideUploadCompanyLogo()} />
					</ModalFooterLeft>
					<ModalFooterRight>
						<Button disabled={fileLists?.length === 0} round loading={loading} data-testid="company-logo-confirm" text="Confirm" submit={() => requestAction()} />
					</ModalFooterRight>
				</ModalFooter>
			</Modal>

			<ModalTemplate
				visible={!!logoError}
				hide={() => setLogoError(null)}
				title="Invalid File"
				message={logoError}
				rightFooterButtonText="Close"
			/>

			<Toast toastList={toastList} />
		</>
	)
}

export default Page

Page.propTypes = {
	setCreateModelTerm: PropTypes.func,
	modelTermEnabled: PropTypes.bool,
	termTemplate: PropTypes.object,
}
